<template>
  <div class="super-admin-college-admins">
    <div class="row mb-30">
      <div class="col-12" style="display: flex; justify-content: space-between">
        <account-type-tabs selected="college-admins" />
        <button class="btn btn-primary" @click="onCreate">
          Invite College Admin
        </button>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="dashboard-card">
          <b-table
            no-local-sorting
            striped
            :fields="columns"
            :items="campusAdmins"
            head-variant="dark"
            responsive
            @sort-changed="sortChanged"
          >
            <template #cell(active)="row">
              <div v-if="row.item.isActive" v-b-tooltip.hover.top="'Active'" class="green-bar" />
              <div v-else v-b-tooltip.hover.top="'Inactive'" class="red-bar" />
            </template>
            <template #cell(name)="row">
              {{ row.item.firstName }} {{ row.item.lastName }}
            </template>
            <template #cell(email)="row">
              {{ row.item.email }}
            </template>
            <template #cell(campus)="row">
              {{ row.item.campus.name }}
            </template>
            <template #cell(last-login)="row">
              {{ row.item.lastLogin | withTimeZone | format('M/D/YYYY h:mmA (z)') }}
            </template>
            <template #cell(actions)="row">
              <div v-if="!row.item._showDetails" class="d-flex">
                <button
                  v-if="row.item.isActive"
                  class="btn btn-ghost w-100"
                  type="button"
                  @click="toggleDeactivateConfirm(row)"
                >
                  Deactivate
                </button>
                <button
                  v-else
                  class="btn btn-ghost w-100"
                  type="button"
                  @click="toggleActivateConfirm(row)"
                >
                  Activate
                </button>
                <button
                  class="btn btn-primary w-100"
                  type="button"
                  @click="$emit('impersonate', row.item.userId);"
                >
                  Impersonate
                </button>
              </div>
            </template>
            <template #row-details="row">
              <div class="row-details-container">
                <div class="message">
                  {{ row.item.message }}
                </div>
                <div class="buttons">
                  <button class="btn btn-warn" type="button" @click="row.item.action(row.item)">
                    Confirm
                  </button>
                  <button class="btn btn-ghost" type="button" @click="row.toggleDetails()">
                    Back
                  </button>
                </div>
              </div>
            </template>
          </b-table>
          <loader v-if="loading" style="width: 100%" />
          <p v-else-if="!campusAdmins.length">
            No records found.
          </p>
          <div v-else-if="!pageToken" class="text-center">
            All records loaded.
          </div>
          <button
            v-else
            type="button"
            class="btn btn-primary"
            @click="fetchNextRecords()"
          >
            Load More
          </button>
        </div>
      </div>
    </div>

    <create-college-admin-modal ref="createCollegeAdminModal" @created="onCreated" />
  </div>
</template>

<script>
import moment from 'moment';
import delay from '@/helpers/mixins/delay';
import AdminClient from '@/api/AdminClient';
import Loader from '@/components/Loader.vue';
import AccountTypeTabs from './components/AccountTypeTabs.vue';
import CreateCollegeAdminModal from './components/CreateCollegeAdminModal.vue';

export default {
  name: 'SuperAdminCollegeAdmins',

  components: {
    Loader,
    AccountTypeTabs,
    CreateCollegeAdminModal,
  },
  mixins:[delay],
  props: {
    search: {
      type: String,
      required: true
    },
    csvBus: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      adminClient: new AdminClient(this.$http),
      
      columns: [
          { key: 'active', label: '' },
          { key: 'name', label: 'Name', sortable: true, sortAsc: 'ByNameAscendingListCampusAdminsPageToken', sortDesc: 'ByNameDescendingListCampusAdminsPageToken' },
          { key: 'email', label: 'Email', sortable: true, sortAsc: 'ByEmailAscendingListCampusAdminsPageToken', sortDesc: 'ByEmailDescendingListCampusAdminsPageToken' },
          { key: 'campus', label: 'Campus', sortable: true, sortAsc: 'ByCollegeAscendingListCampusAdminsPageToken', sortDesc: 'ByCollegeDescendingListCampusAdminsPageToken' },
          { key: 'last-login', label: 'Last Login', sortable: true, sortAsc: 'ByLastLoginAscendingListCampusAdminsPageToken', sortDesc: 'ByLastLoginDescendingListCampusAdminsPageToken' },
          { key: 'actions', label: '' }
      ],

      loading: false,
      acting: false,
      pageToken: 'ByLastLoginDescendingListCampusAdminsPageToken',

      campusAdmins: []
    };
  },

  watch: {
    search() {
      this.delay(
        'campus-admin-search',
        this.reset.bind(this),
        500);
    }
  },

  mounted() {
    this.csvBus.$on('generate', this.generateCsv);
    this.reset(this.pageToken);
  },

  destroyed() {
    this.csvBus.$off('generate', this.generateCsv);
  },

  methods: {
    toggleDeactivateConfirm(row) {
      row.item.action = this.deactivateCampusAdmin.bind(this);
      row.item.message = `Are you sure you want to deactivate this campus admin?`;
      row.toggleDetails();
    },
    async deactivateCampusAdmin(campusAdmin) {
      try {
        this.acting = true;
        const res = await this.adminClient.deactivateCollegeAdmin(campusAdmin.userId)
        this.$store.commit('setGlobalAlert', {
            msg: 'Campus admin deactivated.',
        });
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
            msg: 'Failed to deactivate campus admin. Please try again!', error: true,
        });
      } finally {
        this.acting = false;
      }
      this.reset();
    },
    
    toggleActivateConfirm(row) {
      row.item.action = this.activateCampusAdmin.bind(this);
      row.item.message = `Are you sure you want to activate this campus admin?`;
      row.toggleDetails();
    },
    async activateCampusAdmin(campusAdmin) {
      try {
        this.acting = true;
        const res = await this.adminClient.activateCollegeAdmin(campusAdmin.userId)
        this.$store.commit('setGlobalAlert', {
            msg: 'Campus admin activated.',
        });
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
            msg: 'Failed to activate campus admin. Please try again!', error: true,
        });
      } finally {
        this.acting = false;
      }
      this.reset();
    },
    impersonate(row) {
      this.$emit('impersonate', row.item.userId);
    },
    reset(pageToken) {
      if (!pageToken) {
        pageToken = 'ByLastLoginDescendingListCampusAdminsPageToken'
      }

      this.pageToken = pageToken;
      this.campusAdmins = [];

      return this.fetchNextRecords();
    },
    async fetchNextRecords() {
        try {
            this.loading = true;

            const response = await this.fetch(this.pageToken, 20);
            
            this.pageToken = response.nextPageToken;
            
            response.campusAdmins.forEach(campusAdmin => {
                this.campusAdmins.push({
                    ...campusAdmin,
                    lastLogin: campusAdmin.lastLogin ? moment(campusAdmin.lastLogin) : null,
                });
            });
        } catch (ex) {
            console.error(ex);
        } finally {
            this.loading = false;
        }
    },
    fetch(pageToken, recordCount) {
      let searches = undefined;

      if (this.search) {
        searches = this.search
          .split(/s+/)
          .filter(Boolean)
      }

      return this.adminClient.getCollegeAdminList(
        searches,
        pageToken,
        recordCount
      );
    },

    sortChanged(change) {
      const { sortBy, sortDesc } = change;
      const column = this.columns.find(x => x.key === sortBy);
      if (!column) {
        console.error('Could not find sorting column.')
        return;
      }
      const customSort = sortDesc ? column.sortDesc : column.sortAsc;

      return this.reset(customSort);
    },
    
    async generateCsv() {
      this.csvBus.$emit(
        'error',
        "College Admins can't be exported to CSV, yet."
      );
    },

    onCreate() {
      this.$refs.createCollegeAdminModal.show();
    },
    onCreated() {
      this.reset();
    }
  }
};
</script>

<style lang="scss">
.super-admin-college-admins {
  .b-table {
    color: $white;
    thead.thead-dark th{
        background-color: $dark-blue !important;
        border-color: $border-color !important;
    }

    .red-bar {
        border-radius: 5px;
        width: 5px;
        height: 25px;
        background-color: $red;
    }
    .green-bar {
        border-radius: 5px;
        width: 5px;
        height: 25px;
        background-color: $approval-green;
    }
    .row-details-container {
        margin-bottom: 25px;

        div.message {
            margin-top: 25px;
            margin-bottom: 25px;
        }
    }
    button.btn {
        margin-right: 5px;
    }
    button.btn-warn {
        color: $white;
        background-color: $red;
    }
  }
}
</style>
